/**
 * tRPC Client
 *
 * Provides typed API access to the backend server.
 * Uses vanilla client (not React Query) for simpler integration.
 */

import { createTRPCProxyClient, httpBatchLink } from '@trpc/client';
import type { AppRouter } from '../../../api/src/router';
import { useCampaignStore } from '../stores/campaignStore';
import { supabase } from './supabase';

// =============================================================================
// CLIENT
// =============================================================================

/**
 * tRPC client instance
 *
 * In development: Uses Vite's proxy to forward /trpc to localhost:3334
 * In production: Calls the Render API directly
 */
const API_URL = import.meta.env.VITE_API_URL || '';

export const trpc = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${API_URL}/trpc`,
      async headers() {
        const campaignId = useCampaignStore.getState().currentCampaignId;
        const headers: Record<string, string> = {};

        // Get a fresh session (triggers token refresh if expired)
        const { data: { session } } = await supabase.auth.getSession();

        // Send JWT token for server-side verification
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }

        // Fallback: send user ID directly (server accepts as legacy path)
        if (session?.user?.id) {
          headers['x-user-id'] = session.user.id;
        }

        // Campaign context (not auth — just scoping)
        if (campaignId) {
          headers['x-campaign-id'] = campaignId;
        }

        return headers;
      },
    }),
  ],
});

// =============================================================================
// RE-EXPORT TYPE
// =============================================================================

export type { AppRouter };
